import _RateReview from "@mui/icons-material/RateReview";
import _FullscreenExit from "@mui/icons-material/FullscreenExit";
import _Fullscreen from "@mui/icons-material/Fullscreen";
import _Add from "@mui/icons-material/Add";
import _Remove from "@mui/icons-material/Remove";
import _MyLocation from "@mui/icons-material/MyLocation";
import _ChevronLeft from "@mui/icons-material/ChevronLeft";
import { useTheme } from '@mui/material/styles';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { AttributionControl, Map, NavigationControl, ScaleControl, setWorkerUrl } from 'maplibre-gl';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonControl, CompassIcon, FilterIcon, defaultBaseLayers } from '../../components';
import { BaseLayersControl } from '../../components/map/base-layers-control';
import { AppService, MapService } from '../../services';
import { languages } from '../../translations';
import { getDataFromUrl } from '../../utils';
import { setMapBaseLayer } from './base-layer';
import { embeddedFacilitiesLayerIdSuffix, embeddedFacilitiesMetadataKey, facilitiesSourceIds, getEmbeddedFacilitiesLayerKind, toEmbeddedFacilitiesSourceId } from './facilities';
import { useLayers } from './layers';
import { useSource } from './source';
import spain from './spain';
import switzerland from './switzerland';
import { useTerrain } from './terrain';

// MapLibre 6 no longer inlines its worker: it looks for it next to its own file, which no longer
// exists once bundled. The bundler emits the worker as an asset and we point MapLibre at it.
// (tsc sees this package as CommonJS, hence the directive; Babel emits ESM and so keeps import.meta.)
if (typeof window !== 'undefined')
  // @ts-expect-error TS1470: import.meta is not allowed when tsc assumes CommonJS output
  setWorkerUrl(new URL('maplibre-gl/dist/maplibre-gl-worker.mjs', import.meta.url).href);
const baseSourceId = 'openmaptiles';
const baseLabelsSourceId = `${baseSourceId}_labels`;
const darkSourceId = 'dark';
const tonerSourceId = 'toner';
const darkLabelsSourceId = `${darkSourceId}_labels`;
const geolocationSourceId = 'geolocation';
const exclusionZonesSourceId = 'exclusion_zone';
const dropZonesSourceId = 'exclusion_zone_drop_zones';
export const parkingSourceId = 'parking';
export const poiSourceIds = ['poi_utile', 'poi_tourisme', 'poi_hebergement', 'poi_restaurant'];
const countries = [{
  key: 'switzerland',
  multiPolygon: switzerland
}, {
  key: 'spain',
  multiPolygon: spain
}];
export const baseLayersMap = {
  geovelo: {
    key: 'geovelo',
    labelKey: 'commons.base_layers.geovelo'
  },
  aerial: {
    key: 'aerial',
    labelKey: 'commons.base_layers.aerial',
    url: 'https://data.geopf.fr/wmts?' + '&SERVICE=WMTS' + '&REQUEST=GetTile' + '&VERSION=1.0.0' + '&LAYER=ORTHOIMAGERY.ORTHOPHOTOS' + '&TILEMATRIXSET=PM' + '&TILEMATRIX={z}' + '&TILECOL={x}' + '&TILEROW={y}' + '&STYLE=normal' + '&FORMAT=image/jpeg',
    tileSize: 256,
    attribution: 'IGN-F/Geoportail',
    maxZoom: 18,
    alternatives: {
      switzerland: {
        key: 'aerial_switzerland',
        url: 'https://wmts10.geo.admin.ch/1.0.0/ch.swisstopo.swissimage/default/current/3857/{z}/{x}/{y}.jpeg',
        attribution: 'Source: Office fédéral de topographie'
      },
      spain: {
        key: 'aerial_spain',
        url: 'https://www.ign.es/wmts/pnoa-ma?layer=OI.OrthoimageCoverage&tilematrixset=GoogleMapsCompatible&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fjpeg&TileMatrix={z}&TileCol={x}&TileRow={y}',
        tileSize: 256,
        attribution: 'Source: Instituto Geográfico Nacional'
      }
    }
  },
  ign: {
    key: 'ign',
    labelKey: 'commons.base_layers.ign',
    url: 'https://data.geopf.fr/private/wmts?' + '&SERVICE=WMTS' + '&REQUEST=GetTile' + '&VERSION=1.0.0' + '&LAYER=GEOGRAPHICALGRIDSYSTEMS.MAPS' + '&TILEMATRIXSET=PM' + '&TILEMATRIX={z}' + '&TILEROW={y}' + '&TILECOL={x}' + '&STYLE=normal' + '&FORMAT=image/jpeg' + '&apikey=ign_scan_ws',
    tileSize: 256,
    attribution: 'IGN-F/Geoportail',
    maxZoom: 18,
    alternatives: {
      switzerland: {
        key: 'ign_switzerland',
        url: 'https://wmts10.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-farbe/default/current/3857/{z}/{x}/{y}.jpeg',
        attribution: 'Source: Office fédéral de topographie'
      },
      spain: {
        key: 'ign_spain',
        url: 'https://www.ign.es/wmts/mapa-raster?layer=MTN&tilematrixset=GoogleMapsCompatible&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fjpeg&TileMatrix={z}&TileCol={x}&TileRow={y}',
        tileSize: 256,
        attribution: 'Source: Instituto Geográfico Nacional'
      }
    }
  },
  dark: {
    key: 'dark',
    labelKey: 'commons.base_layers.dark'
  },
  toner: {
    key: 'toner',
    labelKey: 'commons.base_layers.toner'
  }
};
export function useGeoveloMap({
  baseLayers,
  baseLayersPlacement,
  baseLayer: _baseLayer,
  smallDevice,
  layersDrawerOpen,
  currentUser,
  currentGeolocation,
  currentLanguage,
  hillshadesShowed,
  terrainShowed,
  theme: _theme,
  onClick,
  onLayersControlToggle,
  onLanguageButtonClick,
  onContribute,
  handleGeolocationChange,
  handleGeolocationError
} = {}) {
  const [map, setMap] = useState();
  const mapRef = useRef(null);
  const [options, setOptions] = useState();
  const layerOpacities = useRef({});
  const [bounds, setBounds] = useState();
  const [center, setCenter] = useState();
  const [zoom, setZoom] = useState();
  const [initialized, setInitialized] = useState(false);
  const initializedRef = useRef(false);
  const [controlsInitialized, setControlsInitialized] = useState(false);
  const country = useRef('france');
  /** Country the base layer was last applied for — empty until the first application. */
  const appliedCountry = useRef(null);
  const [baseLayer, _setBaseLayer] = useState(_baseLayer || 'geovelo');
  const baseLayerRef = useRef(_baseLayer || 'geovelo');
  const backgroundsLayersRef = useRef({});
  const hillshadesLayersRef = useRef({});
  const baseLayersControlRef = useRef(null);
  const compassControlRef = useRef(null);
  const contributeControlRef = useRef(null);
  const locateControlRef = useRef(null);
  const layersControlRef = useRef(null);
  const attributionControlRef = useRef(null);
  const languagesControlRef = useRef(null);
  const {
    t
  } = useTranslation();
  const defaultTheme = useTheme();
  const theme = _theme ? _theme : defaultTheme;
  const {
    addGeoJSONSource: addGeolocationSource,
    updateGeoJSONSource: updateGeolocationSource
  } = useSource(map, geolocationSourceId);
  const {
    addCircleLayer,
    getLayerOpacities
  } = useLayers(map);
  useTerrain(map, terrainShowed);
  useEffect(() => {
    if (map && !initialized) {
      const [[west, south], [east, north]] = map.getBounds().toArray();
      initLabelsSources();
      initGeolocationSource();
      initControls(map);
      setBounds({
        north,
        east,
        south,
        west
      });
      setCenter(map.getCenter());
      setInitialized(true);
    }
  }, [map]);
  useEffect(() => {
    if (initialized && center) {
      const {
        lat,
        lng
      } = center;
      const point = {
        type: 'Point',
        coordinates: [lng, lat]
      };
      const countryKey = countries.find(({
        multiPolygon
      }) => booleanPointInPolygon(point, multiPolygon))?.key || 'france';

      // This effect runs on every `moveend`, and `setBaseLayer` serializes the whole style and
      // walks each of its layers — affordable on a border crossing, ruinous when the camera moves
      // on every frame of a video render. The ref starts empty so the first pass still applies:
      // it is what puts the raster layer up on a map opened straight on `aerial` or `ign`.
      if (appliedCountry.current === countryKey) return;
      appliedCountry.current = countryKey;
      country.current = countryKey;
      if (baseLayerRef.current !== 'geovelo' && baseLayerRef.current !== 'dark') setBaseLayer(baseLayerRef.current);
    }
  }, [initialized, center]);
  useEffect(() => {
    if (layersControlRef.current) layersControlRef.current.update({
      title: t('commons.actions.configure_layers') || ''
    });
    if (currentLanguage && languagesControlRef.current) {
      const {
        icon,
        label
      } = languages[currentLanguage];
      languagesControlRef.current.update({
        icon,
        title: label
      });
    }
  }, [currentLanguage]);
  useEffect(() => {
    if (controlsInitialized && contributeControlRef.current) {
      contributeControlRef.current.update({}, () => onContribute?.());
    }
  }, [currentUser]);
  function handleLocateControlClick(map) {
    const targetZoom = options?.locateControlZoom ?? 15;
    const flyToAndNotify = (lat, lng) => {
      const onMoveEnd = () => {
        map.off('moveend', onMoveEnd);
        handleGeolocationChange?.({
          lat,
          lng
        });
      };
      map.on('moveend', onMoveEnd);
      map.flyTo({
        center: {
          lat,
          lng
        },
        zoom: targetZoom,
        maxDuration: 1000
      });
    };
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(({
        coords: {
          latitude: lat,
          longitude: lng
        }
      }) => {
        flyToAndNotify(lat, lng);
      }, () => {
        if (!currentGeolocation) {
          if (handleGeolocationError) handleGeolocationError();else console.error('cannot get current position');
          return;
        }
        const {
          point: {
            coordinates: [lng, lat]
          }
        } = currentGeolocation;
        flyToAndNotify(lat, lng);
      });
      return;
    }
    if (currentGeolocation) {
      const {
        point: {
          coordinates: [lng, lat]
        }
      } = currentGeolocation;
      flyToAndNotify(lat, lng);
    } else {
      if (handleGeolocationError) handleGeolocationError();else console.error('cannot get current position');
    }
  }
  useEffect(() => {
    if (controlsInitialized && locateControlRef.current) {
      locateControlRef.current.update({}, map => handleLocateControlClick(map));
    }
  }, [currentGeolocation]);
  useEffect(() => {
    if (initialized && options?.locateControl) {
      updateGeolocationSource({
        type: 'FeatureCollection',
        features: currentGeolocation ? [{
          type: 'Feature',
          geometry: currentGeolocation.point,
          properties: {}
        }] : []
      });
    }
  }, [initialized, currentGeolocation]);
  useEffect(() => {
    if (map && controlsInitialized && smallDevice) {
      if (attributionControlRef.current && map.hasControl(attributionControlRef.current)) map.removeControl(attributionControlRef.current);
      if (attributionControlRef.current) map.addControl(attributionControlRef.current, 'bottom-left');
      if (layersControlRef.current) {
        if (map.hasControl(layersControlRef.current)) map.removeControl(layersControlRef.current);
        map.addControl(layersControlRef.current, 'bottom-left');
        layersControlRef.current.update();
      }
      if (baseLayersControlRef.current) {
        if (map.hasControl(baseLayersControlRef.current)) map.removeControl(baseLayersControlRef.current);
        map.addControl(baseLayersControlRef.current, 'bottom-left');
        baseLayersControlRef.current.update();
      }
    }
  }, [controlsInitialized, smallDevice]);
  useEffect(() => {
    if (layersControlRef.current) {
      if (layersDrawerOpen) {
        layersControlRef.current.update({
          color: 'default',
          icon: /*#__PURE__*/React.createElement(_ChevronLeft, null),
          size: 'medium',
          title: t('commons.actions.close') || '',
          variant: 'round'
        }, () => {
          if (onLayersControlToggle) onLayersControlToggle(false);
        });
      } else {
        layersControlRef.current.update({
          color: 'primary',
          icon: /*#__PURE__*/React.createElement(FilterIcon, null),
          size: smallDevice ? 'medium' : 'large',
          title: t('commons.actions.configure_layers') || '',
          variant: smallDevice ? 'round' : 'extended'
        }, () => {
          if (onLayersControlToggle) onLayersControlToggle(true);
        });
      }
    }
  }, [layersDrawerOpen, smallDevice]);
  useEffect(() => {
    const hillshadesKey = baseLayerRef.current === 'dark' ? 'dark' : baseLayerRef.current === 'toner' ? 'toner' : 'base';
    function removeHillshadesLayers() {
      if (!mapRef.current) return;
      ['base', 'dark', 'toner'].forEach(key => hillshadesLayersRef.current?.[key]?.forEach(({
        id
      }) => map?.getLayer(id) && map.removeLayer(id)));
    }

    // The 3D relief supersedes the flat shading: stacking a baked-in lighting on top of a
    // geometric one muddies the slopes.
    if (hillshadesShowed && !terrainShowed) {
      hillshadesLayersRef.current?.[hillshadesKey]?.forEach(layer => map && !map.getLayer(layer.id) && map.addLayer(layer));
    } else {
      // Skipping the `addLayer` above is not enough: when the hillshades were on as the style was
      // built, its layers already carry them, and no cleanup has run yet to take them off.
      removeHillshadesLayers();
    }
    return removeHillshadesLayers;
    // `map` belongs here: without it the effect keeps the `undefined` it closed over on the first
    // render, and its cleanup then silently removes nothing.
  }, [map, baseLayer, hillshadesShowed, terrainShowed]);
  function hideLayerByDefault(layer) {
    if (layer.type === 'background') return layer;
    return {
      ...layer,
      layout: {
        ...layer.layout,
        visibility: 'none'
      }
    };
  }
  async function getStyle(kind) {
    const {
      tileServer: {
        url: tileServerUrl,
        styleDirs
      }
    } = AppService.environment;
    const styleDir = styleDirs[kind];
    const {
      layers: _layers,
      sources: _sources,
      ...style
    } = await getDataFromUrl(`${tileServerUrl}/styles/${styleDir}/style.json${options?.tileServerSource ? `?source=${encodeURIComponent(options.tileServerSource)}` : ''}`);
    const allowedSourceIds = [baseSourceId, exclusionZonesSourceId, dropZonesSourceId, parkingSourceId, ...poiSourceIds];
    const nativeOverlaySourceIds = [parkingSourceId, ...poiSourceIds];
    const isFacilitiesLayer = layer => 'source' in layer && facilitiesSourceIds.includes(layer.source);
    // the toner style carries no facilities
    const embeddedFacilitiesKind = kind === 'toner' ? null : kind;
    const layers = _layers.filter(layer => isFacilitiesLayer(layer) ? Boolean(embeddedFacilitiesKind) : hillshadesShowed || layer.type === 'background' || 'source' in layer && allowedSourceIds.includes(layer.source))
    // some tileserver style versions bake `gv_*` (parking/POI) layers directly into the base
    // style as `visibility: visible` — hidden here unconditionally, regardless of style version
    // or which app consumes it, so only `useNativeParkingLots`/`useNativePois`'s `toggle()` can
    // ever show them. Without this every app sharing this style would render them, unmanaged.
    // The facilities are hidden the same way, only `useFacilities`'s `toggle()` shows them.
    .map(layer => {
      if ('source' in layer && embeddedFacilitiesKind && isFacilitiesLayer(layer)) {
        return {
          ...hideLayerByDefault(layer),
          id: `${layer.id}${embeddedFacilitiesLayerIdSuffix}`,
          source: toEmbeddedFacilitiesSourceId(layer.source, embeddedFacilitiesKind),
          metadata: {
            ...layer.metadata,
            [embeddedFacilitiesMetadataKey]: embeddedFacilitiesKind
          }
        };
      }
      return 'source' in layer && nativeOverlaySourceIds.includes(layer.source) ? hideLayerByDefault(layer) : layer;
    });
    const sources = {
      ..._sources
    };
    facilitiesSourceIds.forEach(sourceId => {
      const source = sources[sourceId];
      if (!source) return;
      delete sources[sourceId];
      if (embeddedFacilitiesKind) sources[toEmbeddedFacilitiesSourceId(sourceId, embeddedFacilitiesKind)] = source;
    });
    const backgroundLayer = _layers.find(layer => layer.type === 'background');
    const hillshadesLayers = _layers.filter(layer => 'source' in layer && layer.source === 'hillshades');
    return {
      ...style,
      sources,
      layers,
      backgroundLayer,
      hillshadesLayers,
      version: 8 // hack for typing issue
    };
  }
  async function init({
    baseLayer: _baseLayer,
    ...options
  }) {
    if (typeof window !== 'undefined' && typeof options.container === 'string' && !document.getElementById(options.container)) {
      for (let attempt = 0; attempt < 12; attempt += 1) {
        await new Promise(resolve => {
          window.setTimeout(resolve, 16);
        });
        if (document.getElementById(options.container)) break;
      }
      if (!document.getElementById(options.container)) return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;
    setOptions(options);
    if (_baseLayer) {
      baseLayerRef.current = _baseLayer;
      _setBaseLayer(_baseLayer);
    }
    if (options.center === undefined) delete options.center;
    if (options.zoom === undefined) delete options.zoom;
    const {
      scaleControl,
      disableAttributionControl,
      customZoomControls,
      zoomControlsPosition,
      locateControl,
      compassControl,
      fullscreenControl,
      baseLayersControl,
      layersControl,
      contributeControl,
      languagesControl,
      rasterMaxZoom,
      tileServerSource: _tileServerSource,
      ...mapOptions
    } = options;
    const dark = baseLayerRef.current === 'dark';
    let style;
    let sources = {};
    const layers = [];
    if (!baseLayers || baseLayers.includes('geovelo')) {
      MapService.style = await getStyle('base');
      const {
        sources: _sources,
        layers: _layers,
        backgroundLayer,
        hillshadesLayers,
        ..._style
      } = MapService.style;
      backgroundsLayersRef.current['base'] = backgroundLayer;
      hillshadesLayersRef.current['base'] = hillshadesLayers;
      if (!style || baseLayerRef.current === 'geovelo') style = _style;
      sources = _sources;
      const baseLayers = _layers.filter(({
        type
      }) => type !== 'background');
      layers.push(...baseLayers);
      layerOpacities.current = {
        ...layerOpacities.current,
        ...getLayerOpacities(layers, baseSourceId)
      };
    }
    if (baseLayers?.includes('dark')) {
      MapService.darkStyle = await getStyle('dark');
      const {
        sources: _sources,
        layers: _layers,
        backgroundLayer,
        hillshadesLayers,
        ..._style
      } = MapService.darkStyle;
      backgroundsLayersRef.current['dark'] = backgroundLayer;
      hillshadesLayersRef.current['dark'] = hillshadesLayers;
      if (!style || baseLayerRef.current === 'dark') style = _style;
      sources[darkSourceId] = _sources[baseSourceId];
      facilitiesSourceIds.forEach(sourceId => {
        const embeddedSourceId = toEmbeddedFacilitiesSourceId(sourceId, 'dark');
        if (_sources[embeddedSourceId]) sources[embeddedSourceId] = _sources[embeddedSourceId];
      });

      // `parking`/`poi_*` are already merged in from the base style above (baked into it by the
      // tileserver as of style v2-4) — skip them here instead of remapping them onto the dark
      // raster-equivalent source, which doesn't carry this data and would just render nothing
      const nativeOverlaySourceIds = [parkingSourceId, ...poiSourceIds];
      const darkLayers = _layers.filter(layer => layer.type !== 'background' && !('source' in layer && nativeOverlaySourceIds.includes(layer.source)))
      // the embedded facilities keep their own source, the base one has no facilities in it
      .map(layer => ({
        ...layer,
        id: `${layer.id}_dark`,
        ...(getEmbeddedFacilitiesLayerKind(layer) ? {} : {
          source: darkSourceId
        })
      }));
      layers.push(...darkLayers);
      layerOpacities.current = {
        ...layerOpacities.current,
        ...getLayerOpacities(darkLayers, darkSourceId)
      };
      if (dark) toggleLayers(layers, baseSourceId, false, true);else toggleLayers(layers, darkSourceId, false, true);
    }
    if (baseLayers?.includes('toner')) {
      const tonerStyle = MapService.tonerStyle = await getStyle('toner');
      const {
        sources: _sources,
        layers: _layers,
        backgroundLayer,
        hillshadesLayers,
        ..._style
      } = tonerStyle;
      backgroundsLayersRef.current['toner'] = backgroundLayer;
      hillshadesLayersRef.current['toner'] = hillshadesLayers;
      if (!style || baseLayerRef.current === 'toner') style = _style;
      sources[tonerSourceId] = _sources[baseSourceId];
      const tonerLayers = _layers.filter(({
        type
      }) => type !== 'background').map(({
        id,
        ...props
      }) => ({
        ...props,
        id: `${id}_toner`,
        source: tonerSourceId
      }));
      layers.push(...tonerLayers);
      layerOpacities.current = {
        ...layerOpacities.current,
        ...getLayerOpacities(tonerLayers, tonerSourceId)
      };

      // symmetrical to the dark block above: hiding the toner layers is only half the job, the
      // base ones have to go too when toner is the active one, otherwise both vector sources are
      // fetched and rendered, toner painting over base
      if (baseLayerRef.current !== 'toner') toggleLayers(layers, tonerSourceId, false, true);else toggleLayers(layers, baseSourceId, false, true);
    }
    const backgroundKey = baseLayerRef.current === 'dark' ? 'dark' : baseLayerRef.current === 'toner' ? 'toner' : 'base';
    const backgroundLayer = backgroundsLayersRef.current[backgroundKey];
    if (backgroundLayer) layers.splice(0, 0, backgroundLayer);
    [parkingSourceId, ...poiSourceIds].forEach(sourceId => {
      const source = sources[sourceId];
      if (source && source.type === 'vector') sources[sourceId] = {
        ...source,
        promoteId: 'id'
      };
    });
    if (!style) return;

    // the styles above are fetched asynchronously: the container may have been unmounted in the
    // meantime (tab change, redirection...), in which case maplibre would throw
    if (typeof window !== 'undefined' && typeof options.container === 'string' && !document.getElementById(options.container)) {
      initializedRef.current = false;
      return;
    }
    const newMap = new Map({
      style: {
        ...style,
        sources,
        layers
      },
      minZoom: 1,
      maxZoom: 17.99,
      renderWorldCopies: false,
      ...mapOptions,
      attributionControl: false
    });
    const anchorLayerIds = ['basemap', 'facilities', 'partnerLayers', 'labels', '_labels'];
    const addAnchorLayers = map => {
      anchorLayerIds.forEach(id => {
        if (!map.getLayer(id)) {
          map.addLayer({
            id,
            type: 'background',
            paint: {
              'background-opacity': 0
            }
          });
        }
      });
    };
    setMapBaseLayer(newMap, baseLayerRef.current);
    newMap.once('styledata', () => {
      addAnchorLayers(newMap);
    });
    newMap.once('load', async () => {
      mapRef.current = newMap;
      setMap(newMap);
      (baseLayers || defaultBaseLayers).forEach(key => {
        const {
          url,
          tileSize,
          attribution,
          alternatives
        } = baseLayersMap[key];
        if (key === 'aerial' || key === 'ign') {
          newMap.addSource(key, {
            type: 'raster',
            tiles: url ? [url] : undefined,
            tileSize: tileSize || 256,
            attribution,
            ...(rasterMaxZoom ? {
              maxzoom: rasterMaxZoom
            } : {})
          });
          if (alternatives) {
            Object.values(alternatives).forEach(({
              key: alternativeKey,
              url: alternativeUrl,
              tileSize: alternativeTileSize,
              attribution: alternativeAttribution
            }) => {
              newMap.addSource(alternativeKey, {
                type: 'raster',
                tiles: alternativeUrl ? [alternativeUrl] : undefined,
                tileSize: alternativeTileSize || 256,
                attribution: alternativeAttribution,
                ...(rasterMaxZoom ? {
                  maxzoom: rasterMaxZoom
                } : {})
              });
            });
          }
        }
      });
    });
    if (!disableAttributionControl) {
      attributionControlRef.current = new AttributionControl();
      newMap.addControl(attributionControlRef.current, smallDevice ? 'bottom-left' : 'bottom-right');
    }
    if (onClick) {
      newMap.on('click', ({
        lngLat: {
          lat,
          lng
        }
      }) => {
        onClick({
          type: 'Point',
          coordinates: [lng, lat]
        });
      });
    }
    newMap.on('moveend', () => {
      const [[west, south], [east, north]] = newMap.getBounds().toArray();
      setBounds({
        north,
        east,
        south,
        west
      });
      const {
        lat,
        lng
      } = newMap.getCenter();
      setCenter({
        lat,
        lng
      });
      setZoom(newMap.getZoom());
    });
  }
  function destroy() {
    map?.remove();
    mapRef.current = null;
    setMap(undefined);
    setInitialized(false);
    initializedRef.current = false;
  }
  function toggleLayers(layers = [], sourceId, showed, init = false) {
    layers.forEach(layer => {
      if ('source' in layer && layer.source === sourceId) {
        const layerOpacity = layerOpacities.current[layer.id];
        const opacity = showed ? layerOpacity !== undefined ? layerOpacity : 1 : 0;
        if (layer.type === 'fill') {
          if (init && layer.paint) layer.paint['fill-opacity'] = opacity;else map?.setPaintProperty(layer.id, 'fill-opacity', opacity);
        } else if (layer.type === 'line') {
          if (init && layer.paint) layer.paint['line-opacity'] = opacity;else map?.setPaintProperty(layer.id, 'line-opacity', opacity);
        } else if (layer.type === 'symbol') {
          if (init && layer.paint) {
            layer.paint['icon-opacity'] = opacity;
            layer.paint['text-opacity'] = opacity;
          } else {
            map?.setPaintProperty(layer.id, 'icon-opacity', opacity);
            map?.setPaintProperty(layer.id, 'text-opacity', opacity);
          }
        } else {
          const visibility = showed ? 'visible' : 'none';
          if (init && layer.layout) layer.layout.visibility = visibility;
          map?.setLayoutProperty(layer.id, 'visibility', visibility);
        }
      }
    });
  }
  async function setBaseLayer(key) {
    const layers = map?.getStyle().layers;
    let rasterLayerId;
    if (key === 'aerial' || key === 'ign') {
      toggleLayers(layers, baseSourceId, false);
      toggleLayers(layers, darkSourceId, false);
      toggleLayers(layers, tonerSourceId, false);
      toggleLayers(layers, darkLabelsSourceId, false);
      if (key === 'ign') {
        toggleLayers(layers, baseLabelsSourceId, false);
      } else {
        toggleLayers(layers, baseLabelsSourceId, true);
      }
      map?.setPaintProperty('background', 'background-color', backgroundsLayersRef.current.base?.paint?.['background-color']);
      const _zoom = map?.getZoom();
      const _baseLayer = baseLayersMap[key];
      const {
        key: id,
        maxZoom
      } = _zoom && _zoom >= 9 && _baseLayer.alternatives?.[country.current] || _baseLayer;
      if (map && !map.getLayer(id)) {
        // Drawn right above the background rather than on top of the base style: the vector
        // layers it would cover are hidden anyway, and the ones left visible (facilities,
        // exclusion zones) then show over the imagery.
        const firstLayerId = map.getStyle().layers.find(({
          type
        }) => type !== 'background')?.id;
        map.addLayer({
          id,
          type: 'raster',
          source: id,
          maxzoom: maxZoom || 22
        }, firstLayerId || 'basemap');
      }
      rasterLayerId = id;
    } else if (key === 'dark') {
      toggleLayers(layers, baseSourceId, false);
      toggleLayers(layers, baseLabelsSourceId, false);
      toggleLayers(layers, tonerSourceId, false);
      map?.setPaintProperty('background', 'background-color', backgroundsLayersRef.current.dark?.paint?.['background-color']);
      toggleLayers(layers, darkSourceId, true);
      toggleLayers(layers, darkLabelsSourceId, true);
    } else if (key === 'toner') {
      toggleLayers(layers, baseSourceId, false);
      toggleLayers(layers, darkSourceId, false);
      toggleLayers(layers, darkLabelsSourceId, false);
      toggleLayers(layers, tonerSourceId, true);
      map?.setPaintProperty('background', 'background-color', backgroundsLayersRef.current.toner?.paint?.['background-color']);
      toggleLayers(layers, baseLabelsSourceId, true);
    } else {
      if (baseLayerRef.current === 'dark') {
        toggleLayers(layers, darkSourceId, false);
        toggleLayers(layers, darkLabelsSourceId, false);
        map?.setPaintProperty('background', 'background-color', backgroundsLayersRef.current.base?.paint?.['background-color']);
      } else if (baseLayerRef.current === 'toner') {
        toggleLayers(layers, tonerSourceId, false);
        map?.setPaintProperty('background', 'background-color', backgroundsLayersRef.current.base?.paint?.['background-color']);
      }
      toggleLayers(layers, baseSourceId, true);
      toggleLayers(layers, baseLabelsSourceId, true);
    }
    if (baseLayerRef.current === 'aerial' || baseLayerRef.current === 'ign') {
      const alternatives = baseLayersMap[baseLayerRef.current].alternatives;
      if (baseLayerRef.current !== rasterLayerId && map?.getLayer(baseLayerRef.current)) map.removeLayer(baseLayerRef.current);
      if (alternatives) {
        Object.values(alternatives).forEach(({
          key: alternativeKey
        }) => {
          if (alternativeKey !== rasterLayerId && map?.getLayer(alternativeKey)) map.removeLayer(alternativeKey);
        });
      }
    }
    baseLayerRef.current = key;
    _setBaseLayer(key);
    if (map) setMapBaseLayer(map, key);
    baseLayersControlRef.current?.update({
      baseLayer: key
    });
  }
  async function initLabelSource(dark) {
    const {
      tileServer: {
        url: tileServerUrl,
        styleDirs: {
          labels: styleDir,
          darkLabels: darkStyleDir
        }
      }
    } = AppService.environment;
    const labelsSourceId = dark ? darkLabelsSourceId : baseLabelsSourceId;
    let labelsStyle;
    if (dark) {
      labelsStyle = MapService.darkLabelsStyle = MapService.darkLabelsStyle || (await getDataFromUrl(`${tileServerUrl}/styles/${darkStyleDir}/style.json`));
    } else {
      labelsStyle = MapService.labelsStyle = MapService.labelsStyle || (await getDataFromUrl(`${tileServerUrl}/styles/${styleDir}/style.json`));
    }
    const {
      sources,
      layers
    } = labelsStyle;
    if (!map?.getSource(labelsSourceId)) map?.addSource(labelsSourceId, sources[baseSourceId]);
    layers.forEach(layer => {
      if (!('source' in layer)) return;
      layer.source = labelsSourceId;
      if (dark) layer.id = `${layer.id}_dark`;
    });
    layerOpacities.current = {
      ...layerOpacities.current,
      ...getLayerOpacities(layers, labelsSourceId)
    };
    if (baseLayers?.includes('dark') && baseLayerRef.current === 'dark' && !dark) toggleLayers(layers, labelsSourceId, false, true);else if (baseLayers?.includes('dark') && baseLayerRef.current !== 'dark' && dark) toggleLayers(layers, labelsSourceId, false, true);
    if (!map?.getLayer('_labels')) return;
    layers.forEach(layer => {
      if (map && !map.getLayer(layer.id)) map.addLayer(layer, '_labels');
    });
  }
  async function initLabelsSources() {
    if (!baseLayers || baseLayers.includes('geovelo')) initLabelSource(false);
    if (baseLayers?.includes('dark')) initLabelSource(true);
  }
  function initGeolocationSource() {
    addGeolocationSource();
    addCircleLayer('geolocation-border', geolocationSourceId, {
      'circle-radius': 10,
      'circle-color': '#2196f3',
      'circle-opacity': 0.5
    });
    addCircleLayer('geolocation-background', geolocationSourceId, {
      'circle-radius': 6,
      'circle-color': 'white',
      'circle-opacity': 1
    });
    addCircleLayer('geolocation-forgeround', geolocationSourceId, {
      'circle-radius': 4,
      'circle-color': '#2196f3',
      'circle-opacity': 1
    });
  }
  function initControls(initializedMap) {
    if (!options) return;
    const {
      scaleControl: showScaleControl,
      container,
      buttonControlsClassName,
      customZoomControls: customZoomControlsEnabled,
      zoomControlsPosition,
      locateControl: locateControlEnabled,
      compassControl: compassControlEnabled,
      fullscreenControl: fullscreenControlEnabled,
      baseLayersControl: baseLayersControlEnabled,
      layersControl: layersControlEnabled,
      contributeControl: contributeControlEnabled,
      languagesControl: languagesControlEnabled
    } = options;
    function addPrefix(suffix) {
      const prefix = typeof container === 'string' ? container : container.id;
      return `${prefix}-${suffix}`;
    }

    // Scale control
    if (showScaleControl) {
      const scaleControl = new ScaleControl({
        maxWidth: 100,
        unit: 'imperial'
      });
      initializedMap.addControl(scaleControl, 'bottom-right');
      scaleControl.setUnit('metric');
    }

    // Locate control — added before the zoom controls below because MapLibre prepends (not
    // appends) new controls in bottom corners, so add-order and final visual stacking order end
    // up reversed: adding this one first means it renders last, ie. below the zoom controls.
    if (locateControlEnabled && 'geolocation' in navigator) {
      locateControlRef.current = new ButtonControl(addPrefix('locate-control'), {
        className: buttonControlsClassName,
        icon: /*#__PURE__*/React.createElement(_MyLocation, null),
        title: t('commons.actions.locate_me')
      }, map => handleLocateControlClick(map));
      initializedMap.addControl(locateControlRef.current, 'bottom-right');
      locateControlRef.current.init(theme);
    }

    // Zoom controls
    if (customZoomControlsEnabled) {
      const zoomOutControl = new ButtonControl(addPrefix('zoom-out-control'), {
        className: buttonControlsClassName,
        icon: /*#__PURE__*/React.createElement(_Remove, null),
        title: t('commons.actions.zoom_out')
      }, map => map.zoomOut());
      initializedMap.addControl(zoomOutControl, zoomControlsPosition || 'bottom-right');
      zoomOutControl.init(theme);
      const zoomInControl = new ButtonControl(addPrefix('zoom-in-control'), {
        className: buttonControlsClassName,
        icon: /*#__PURE__*/React.createElement(_Add, null),
        title: t('commons.actions.zoom_in')
      }, map => map.zoomIn());
      initializedMap.addControl(zoomInControl, zoomControlsPosition || 'bottom-right');
      zoomInControl.init(theme);
    } else if (zoomControlsPosition) {
      const navControl = new NavigationControl({});
      initializedMap.addControl(navControl, zoomControlsPosition);
    }

    // Compass control
    if (compassControlEnabled) {
      compassControlRef.current = new ButtonControl(addPrefix('compass-control'), {
        className: buttonControlsClassName,
        icon: /*#__PURE__*/React.createElement(CompassIcon, null),
        title: t('commons.actions.reset_bearing')
      }, map => map.resetNorthPitch());
      initializedMap.addControl(compassControlRef.current, 'bottom-right');
      compassControlRef.current.init(theme);

      // Rotation and tilt are on by default, so the map can already be turned around by dragging
      // with the right button. Following the heading is what tells the user this button is the way
      // back to a north-up, flat map.
      const followBearing = () => compassControlRef.current?.setIconRotation(-initializedMap.getBearing());
      initializedMap.on('rotate', followBearing);
      followBearing();
    }

    // Fullscreen control
    if (fullscreenControlEnabled && document.body?.requestFullscreen) {
      const fullscreenControl = new ButtonControl(addPrefix('fullscreen-control'), {
        className: buttonControlsClassName,
        icon: /*#__PURE__*/React.createElement(_Fullscreen, null),
        title: t('commons.actions.enter_fullscreen')
      }, () => {
        if (!document.fullscreenElement) {
          document.body.requestFullscreen();
        } else if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      });
      initializedMap.addControl(fullscreenControl, 'bottom-right');
      fullscreenControl.init(theme);
      document.addEventListener('fullscreenchange', () => {
        if (document.fullscreenElement) {
          fullscreenControl.update({
            className: buttonControlsClassName,
            icon: /*#__PURE__*/React.createElement(_FullscreenExit, null),
            title: t('commons.actions.exit_fullscreen') || ''
          });
        } else {
          fullscreenControl.update({
            className: buttonControlsClassName,
            icon: /*#__PURE__*/React.createElement(_Fullscreen, null),
            title: t('commons.actions.enter_fullscreen') || ''
          });
        }
      });
    }

    // Contribute control
    if (contributeControlEnabled) {
      contributeControlRef.current = new ButtonControl(addPrefix('contribute-control'), {
        className: buttonControlsClassName,
        color: 'secondary',
        icon: /*#__PURE__*/React.createElement(_RateReview, null),
        title: t('commons.actions.contribute')
      }, () => onContribute?.());
      initializedMap.addControl(contributeControlRef.current, 'bottom-right');
      contributeControlRef.current.init(theme);
    }

    // Layers control
    if (layersControlEnabled) {
      layersControlRef.current = new ButtonControl(addPrefix('layers-control'), {
        className: buttonControlsClassName,
        color: 'primary',
        icon: /*#__PURE__*/React.createElement(FilterIcon, null),
        size: smallDevice ? 'medium' : 'large',
        title: t('commons.actions.configure_layers'),
        tooltipPlacement: 'right',
        variant: smallDevice ? 'round' : 'extended'
      }, () => {
        if (onLayersControlToggle) onLayersControlToggle(true);
      });
      initializedMap.addControl(layersControlRef.current, 'bottom-left');
      layersControlRef.current.init(theme);
    } else if (baseLayersControlEnabled) {
      baseLayersControlRef.current = new BaseLayersControl({
        baseLayers,
        baseLayer: baseLayerRef.current,
        baseLayersPlacement
      }, baseLayer => setBaseLayer(baseLayer));
      initializedMap.addControl(baseLayersControlRef.current, baseLayersPlacement || 'bottom-left');
      baseLayersControlRef.current.init(theme);
    }

    // Languages control
    if (languagesControlEnabled && currentLanguage) {
      const {
        icon,
        label
      } = languages[currentLanguage];
      languagesControlRef.current = new ButtonControl(addPrefix('languages-control'), {
        ariaControls: 'languages-menu',
        ariaHasPopup: true,
        className: buttonControlsClassName,
        icon,
        title: label
      }, (_, event) => {
        if (onLanguageButtonClick) onLanguageButtonClick(event);
      });
      initializedMap.addControl(languagesControlRef.current, 'bottom-right');
      languagesControlRef.current.init(theme);
    }
    setControlsInitialized(true);
  }
  return {
    map,
    initialized,
    bounds,
    zoom,
    baseLayer,
    init,
    destroy,
    setBaseLayer
  };
}
export * from './bike-stations';
export * from './cyclability-zones';
export * from './clusters';
export * from './facilities';
export * from './game-pmtiles';
export * from './game-tiles';
export * from './layers';
export * from './mapillary';
export * from './native-parking';
export * from './native-pois';
export * from './osrm-routing';
export * from './osrm-routing-style';
export * from './panoramax';
export * from './panoramax-viewer-resize';
export * from './parking';
export * from './partner-layers';
export * from './pois';
export * from './popup';
export * from './reports';
export * from './route-reports';
export * from './rides';
export * from './routing';
export * from './source';
export * from './terrain';
export * from './trace-replay';
export * from './trace-replay-player';
export * from './trace-video';
export * from './traces';
export * from './trip';